import React, { useState } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const StaffGallery = () => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [uploadHistory, setUploadHistory] = useState([]);
    const { config } = useConfig();

    const primaryColor = config?.primaryColor || '#1e40af';

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);
        setUploadProgress({ current: 0, total: files.length });
    };

    const uploadSequentially = async () => {
        if (selectedFiles.length === 0) return;

        setIsUploading(true);
        const total = selectedFiles.length;
        const newHistory = [];

        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            setUploadProgress({ current: i + 1, total });

            const formData = new FormData();
            formData.append('image', file);

            try {
                const response = await api.post('/gallery/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                newHistory.unshift({
                    name: file.name,
                    status: 'success',
                    url: response.data.url,
                    time: new Date().toLocaleTimeString()
                });
            } catch (error) {
                console.error(`Error uploading ${file.name}:`, error);
                newHistory.unshift({
                    name: file.name,
                    status: 'error',
                    message: error.message,
                    time: new Date().toLocaleTimeString()
                });
            }
            // Small delay to ensure "cycles" and avoid sudden peaks, though the sequental nature already handles this
            await new Promise(r => setTimeout(r, 500));
        }

        setUploadHistory(prev => [...newHistory, ...prev]);
        setIsUploading(false);
        setSelectedFiles([]);
        setUploadProgress({ current: 0, total: 0 });
        alert('Proceso de carga finalizado');
    };

    return (
        <div className="animate-fadeIn pb-20">
            <header className="mb-10">
                <div className="inline-block p-4 bg-white rounded-3xl shadow-lg border border-slate-50 text-3xl mb-4">
                    📸
                </div>
                <h1 className="text-4xl font-black text-slate-900 tracking-tight">Carga de Galería</h1>
                <p className="text-slate-500 font-medium mt-1">Sube fotos del evento. Se aplicará marca de agua automáticamente.</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Upload Section */}
                <div className="bg-white p-10 rounded-[40px] border border-slate-100 shadow-xl shadow-slate-200/50">
                    <div 
                        className={`relative border-4 border-dashed rounded-[35px] p-10 flex flex-col items-center justify-center transition-all ${
                            isUploading ? 'border-blue-100 bg-blue-50/10' : 'border-slate-100 hover:border-blue-200 bg-slate-50/30'
                        }`}
                    >
                        <input 
                            type="file" 
                            multiple 
                            accept="image/*"
                            onChange={handleFileSelect}
                            disabled={isUploading}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                        <span className="text-5xl mb-4">📂</span>
                        <p className="text-slate-400 font-black text-xs uppercase tracking-widest text-center">
                            {selectedFiles.length > 0 
                                ? `${selectedFiles.length} archivos seleccionados` 
                                : 'Arrastra o haz clic para seleccionar fotos'}
                        </p>
                    </div>

                    {selectedFiles.length > 0 && !isUploading && (
                        <button 
                            onClick={uploadSequentially}
                            className="w-full mt-8 py-5 rounded-3xl bg-slate-900 text-white font-black uppercase tracking-widest text-xs hover:bg-slate-800 transition-all flex items-center justify-center gap-3 shadow-xl"
                        >
                            Comenzar Carga Secuencial 🚀
                        </button>
                    )}

                    {isUploading && (
                        <div className="mt-8 space-y-4">
                            <div className="flex justify-between items-end mb-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Procesando Ciclo...</span>
                                <span className="text-[10px] font-black text-slate-400">{uploadProgress.current} / {uploadProgress.total}</span>
                            </div>
                            <div className="h-4 bg-slate-100 rounded-full overflow-hidden p-1">
                                <div 
                                    className="h-full bg-blue-600 rounded-full transition-all duration-500 shadow-inner"
                                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                                ></div>
                            </div>
                            <p className="text-center text-[10px] font-bold text-slate-400 animate-pulse">
                                Aplicando marca de agua y optimizando...
                            </p>
                        </div>
                    )}
                </div>

                {/* History Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-400 px-2">Recién subidas</h3>
                    <div className="max-h-[500px] overflow-y-auto pr-2 space-y-3 custom-scrollbar">
                        {uploadHistory.length > 0 ? (
                            uploadHistory.map((item, idx) => (
                                <div key={idx} className="bg-white p-4 rounded-3xl border border-slate-100 flex items-center gap-4 animate-slideIn">
                                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-xl shadow-sm ${
                                        item.status === 'success' ? 'bg-emerald-50' : 'bg-red-50'
                                    }`}>
                                        {item.status === 'success' ? '✅' : '❌'}
                                    </div>
                                    <div className="flex-1 overflow-hidden">
                                        <p className="text-xs font-bold text-slate-800 truncate">{item.name}</p>
                                        <p className="text-[9px] font-black text-slate-300 uppercase tracking-tighter">{item.time}</p>
                                    </div>
                                    {item.status === 'success' && (
                                        <a href={item.url} target="_blank" rel="noreferrer" className="px-3 py-1 bg-slate-50 rounded-lg text-[8px] font-black uppercase hover:bg-blue-50 hover:text-blue-600 transition-colors">
                                            Ver ↗
                                        </a>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="bg-slate-50/50 border border-dashed border-slate-200 rounded-[35px] py-20 flex flex-col items-center justify-center opacity-50">
                                <span className="text-2xl grayscale">⌛</span>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">No hay subidas recientes</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffGallery;
